import moment from 'moment';

class CreateProjectModalController {
  constructor($scope, $uibModalInstance, $rootScope, $http, $timeout, resolvedContractId, resolvedContracts) {
    "ngInject";

    this.$scope = $scope;
    this.$uibModalInstance = $uibModalInstance;
    this.$rootScope = $rootScope;
    this.$http = $http;
    this.$timeout = $timeout;

    // Store resolved contract ID for disabling dropdown
    this.resolvedContractId = resolvedContractId || null;

    // Initialize project object
    this.project = {
      number: '',
      name: '',
      description: '',
      contractId: null,
      startDate: null,
      completionDate: null
    };

    // Date text for display
    this.startDateText = '';
    this.completionDateText = '';
    this.completionDateError = false;
    
    // Cached completion date min date (to avoid infinite digest loops)
    this.completionDateMinDate = this.minDate;

    // Contract selection
    this.selectedContract = null;
    this.contracts = resolvedContracts || [];
    this.periodsOfPerformance = [];
    this.selectedPeriod = null;

    // Date constraints
    this.minDate = moment().subtract(10, 'years').toDate();
    this.maxDate = moment().add(10, 'years').toDate();

    // Load contracts if not provided
    if (!this.contracts || this.contracts.length === 0) {
      this.loadContracts().then(() => {
        // Pre-select contract if provided after contracts are loaded
        if (this.resolvedContractId) {
          const contract = this.contracts.find(c => c.id === this.resolvedContractId);
          if (contract) {
            this.selectedContract = contract;
            this.project.contractId = contract.id;
            this.loadPeriodsOfPerformance();
          }
        }
      });
    } else {
      // Pre-select contract if provided and contracts are already available
      if (this.resolvedContractId) {
        const contract = this.contracts.find(c => c.id === this.resolvedContractId);
        if (contract) {
          this.selectedContract = contract;
          this.project.contractId = contract.id;
          this.loadPeriodsOfPerformance();
        }
      }
    }
  }

  loadContracts() {
    return this.$http.get(this.$rootScope.apiHost + "/api/admin/contracts", { withCredentials: true })
      .then((response) => {
        this.contracts = response.data;
        // If contractId was provided, try to find and select it
        if (this.resolvedContractId) {
          const contract = this.contracts.find(c => c.id === this.resolvedContractId);
          if (contract) {
            this.selectedContract = contract;
            this.project.contractId = contract.id;
            this.loadPeriodsOfPerformance();
          }
        }
      })
      .catch((error) => {
        console.error('Error loading contracts:', error);
      });
  }

  onContractSelected(contract) {
    if (contract) {
      this.selectedContract = contract;
      this.project.contractId = contract.id;
      this.loadPeriodsOfPerformance();
    } else {
      this.selectedContract = null;
      this.project.contractId = null;
      this.periodsOfPerformance = [];
      this.selectedPeriod = null;
    }
  }

  loadPeriodsOfPerformance() {
    if (!this.selectedContract || !this.selectedContract.id) {
      this.periodsOfPerformance = [];
      return;
    }

    // Load contract details to get periods of performance
    this.$http.get(this.$rootScope.apiHost + "/api/admin/contracts/" + this.selectedContract.id, { withCredentials: true })
      .then((response) => {
        const contract = response.data;
        this.periodsOfPerformance = contract.periodsOfPerformance || [];
      })
      .catch((error) => {
        console.error('Error loading periods of performance:', error);
        this.periodsOfPerformance = [];
      });
  }

  getSortedPeriodsOfPerformance() {
    if (!this.periodsOfPerformance || this.periodsOfPerformance.length === 0) {
      return [];
    }
    
    // Sort by end date descending
    return [...this.periodsOfPerformance].sort((a, b) => {
      const dateA = moment(a.endDate).valueOf();
      const dateB = moment(b.endDate).valueOf();
      return dateB - dateA; // Descending order
    });
  }

  onPeriodSelected() {
    if (this.selectedPeriod) {
      // Pre-populate start date and completion date from period of performance
      if (this.selectedPeriod.startDate) {
        const startDate = moment(this.selectedPeriod.startDate).toDate();
        this.project.startDate = moment(startDate).format('YYYY-MM-DD');
        this.startDateText = moment(startDate).format('MMM DD, YYYY');
        
        // Update completion date min date (day after start date)
        this.completionDateMinDate = moment(this.project.startDate).add(1, 'day').toDate();
      }
      
      if (this.selectedPeriod.endDate) {
        const endDate = moment(this.selectedPeriod.endDate).toDate();
        const endMoment = moment(endDate);
        
        // Validate that end date is after start date
        if (this.project.startDate) {
          const startMoment = moment(this.project.startDate);
          if (endMoment.isAfter(startMoment)) {
            this.project.completionDate = endMoment.format('YYYY-MM-DD');
            this.completionDateText = endMoment.format('MMM DD, YYYY');
            this.completionDateError = false;
          } else {
            this.completionDateError = true;
          }
        } else {
          this.project.completionDate = endMoment.format('YYYY-MM-DD');
          this.completionDateText = endMoment.format('MMM DD, YYYY');
        }
      }
    } else {
      // Clear dates if period is deselected
      // Don't clear if user has manually set dates
    }
  }

  onStartDateSelected(selectedDate) {
    if (selectedDate && selectedDate.selectedDate) {
      selectedDate = selectedDate.selectedDate;
    }
    
    if (selectedDate) {
      const date = moment(selectedDate).toDate();
      this.project.startDate = moment(date).format('YYYY-MM-DD');
      this.startDateText = moment(date).format('MMM DD, YYYY');
      
      // Update completion date min date (day after start date)
      this.completionDateMinDate = moment(this.project.startDate).add(1, 'day').toDate();
      
      // If completion date is before or equal to start date, clear it and show error
      if (this.project.completionDate) {
        const completionMoment = moment(this.project.completionDate);
        const startMoment = moment(this.project.startDate);
        
        if (completionMoment.isBefore(startMoment) || completionMoment.isSame(startMoment, 'day')) {
          this.project.completionDate = null;
          this.completionDateText = '';
          this.completionDateError = true;
        } else {
          this.completionDateError = false;
        }
      }
    }
  }

  onCompletionDateSelected(selectedDate) {
    if (selectedDate && selectedDate.selectedDate) {
      selectedDate = selectedDate.selectedDate;
    }
    
    if (selectedDate) {
      const date = moment(selectedDate).toDate();
      const completionMoment = moment(date);
      
      // Validate that completion date is after start date
      if (this.project.startDate) {
        const startMoment = moment(this.project.startDate);
        
        if (completionMoment.isBefore(startMoment) || completionMoment.isSame(startMoment, 'day')) {
          this.completionDateError = true;
          this.project.completionDate = null;
          this.completionDateText = '';
          return;
        }
      }
      
      this.completionDateError = false;
      this.project.completionDate = completionMoment.format('YYYY-MM-DD');
      this.completionDateText = completionMoment.format('MMM DD, YYYY');
    }
  }


  createProject() {
    // Validate form
    if (!this.selectedContract || !this.project.number || !this.project.name) {
      return;
    }

    // Prepare project data
    const projectData = {
      contractId: this.project.contractId,
      number: this.project.number.trim(),
      name: this.project.name.trim(),
      description: this.project.description ? this.project.description.trim() : null,
      startDate: this.project.startDate || null,
      completionDate: this.project.completionDate || null
    };

    // Create project
    this.$http.post(this.$rootScope.apiHost + "/api/admin/projects", projectData, { withCredentials: true })
      .then((response) => {
        this.$uibModalInstance.close(response.data);
      })
      .catch((error) => {
        console.error('Error creating project:', error);
        let errorMessage = 'Failed to create project.';
        if (error.data && error.data.message) {
          errorMessage = error.data.message;
        }
        this.errorMessage = errorMessage;
        this.$timeout(() => {
          this.errorMessage = '';
        }, 5000);
      });
  }

  cancel() {
    this.$uibModalInstance.dismiss('cancel');
  }
}

export default CreateProjectModalController;



// WEBPACK FOOTER //
// ./client/common/modals/createProjectModal/createProjectModal.controller.js